import React, { useRef, useMemo, useCallback, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ProductKeys } from '@atlassiansox/asc-stargate';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import { fireHoldoutExclusionAnalytics } from '@/components/signup/jira-fy27h1-holdout-exclusion-trigger/jira-fy27h1-holdout-exclusion-analytics';
import {
	getIdentityBaseUrl,
	getIdSwitchUserUrl,
	getWacIntegrationUrl,
} from '@/libraries/signup/bxpkit-core/src/utils/environment';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { useLoggedInUser } from '../../utils/logged-in-user-provider';
import { useSitesContext } from '../sites-provider';

import ProductHeader from './components/product-header';
import SiteSelectorModal from './site-selector-modal';

export type TemplateData = {
	templateId: string;
	templateName: string;
	productKey: ProductKeys;
};

export type SiteSelectorContextType = {
	readonly openModalForTemplate: (templateData: TemplateData, isCallOnProductNav?: boolean) => void;
};

export type SiteSelectorContextProviderProps = {
	readonly productKey: ProductKeys;
	readonly analyticsPrefix: string;
	readonly redirectToIdentity?: boolean;
	readonly internalLinkAfterIdentity?: string;
	readonly children?: React.ReactNode;
	readonly selectedTemplateLabel?: string;
	readonly referer?: string;
};

// Site Selector Context
export const SiteSelectorContext = React.createContext<SiteSelectorContextType | null>(null);

export const useSiteSelectorContext = () => {
	const context = React.useContext(SiteSelectorContext);
	if (!context) {
		throw new Error('useSiteSelectorContext must be used within a SiteSelectorContextProvider');
	}
	return context;
};

export const SiteSelectorContextProvider = ({
	productKey,
	children,
	internalLinkAfterIdentity,
	redirectToIdentity = false,
	selectedTemplateLabel,
	analyticsPrefix,
	referer = 'wacdirect',
}: SiteSelectorContextProviderProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const selectedTemplate = useRef<TemplateData | null>(null);
	const userProfile = useLoggedInUser();
	const { sites: aisSites } = useSitesContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();
	const localizedSignupHref = useLocalizedHref('/try/cloud/signup');
	const siteUrls = useMemo(
		() =>
			aisSites?.filter((site) => site.products.includes(productKey)).map((site) => site.url) ?? [],
		[aisSites, productKey],
	);

	const isJiraProduct = (productKey: ProductKeys) => {
		return [
			ProductKeys.JIRA_CORE,
			ProductKeys.JIRA_SERVICE_DESK,
			ProductKeys.JIRA_SOFTWARE,
			ProductKeys.JIRA_PRODUCT_DISCOVERY,
		].includes(productKey);
	};

	const getEventName = (eventName: string): string => {
		return `${analyticsPrefix}.${eventName}`;
	};

	const getCloudSignupUrl = useCallback(() => {
		if (!selectedTemplate.current) {
			return '';
		}
		const { templateId, templateName, productKey } = selectedTemplate.current;
		let url = `${localizedSignupHref}?product=${productKey}&templateId=${templateId}&edition=free`;

		if (templateId === 'jsw.agentic-development-with-cmp') {
			const jiraAiSignupUrl = new URL(localizedSignupHref, location.origin);
			jiraAiSignupUrl.searchParams.append('signupSource', 'jira-ai-native');
			return jiraAiSignupUrl.toString();
		}

		if (isJiraProduct(productKey)) {
			const urlParams = new URLSearchParams(location.search);
			if (!urlParams.has('p')) {
				urlParams.append('templateModal', templateId);
			}
			url += `&continue=${encodeURIComponent(`${location.href}?${urlParams.toString()}`)}`;
		} else if (productKey === ProductKeys.CONFLUENCE) {
			const continueUrl = `${getWacIntegrationUrl()}${internalLinkAfterIdentity ?? ''}?templateId=${
				templateId + encodeURIComponent('&templateName=' + encodeURIComponent(templateName))
			}`;
			url = `${url}&continue=${encodeURIComponent(continueUrl)}`;
		}

		return url;
	}, [localizedSignupHref, internalLinkAfterIdentity]);

	const getIdentityUrl = useCallback(
		(templateId: string, templateName: string) => {
			const loginUrl = `${getIdentityBaseUrl()}/login?`;
			const continueUrl = `${getWacIntegrationUrl()}${
				internalLinkAfterIdentity ?? ''
			}?templateName=${encodeURIComponent(templateName)}&templateId=${templateId}`;

			const isWacIcLoginEnabled = checkFeatureFlagGate(featureFlags.wacIcLogin);
			const applicationParam = isWacIcLoginEnabled ? '&application=wac--ic' : '';

			const finalLoginUrl = `${loginUrl}&continue=${encodeURIComponent(
				continueUrl,
			)}${applicationParam}`;
			return finalLoginUrl;
		},
		[internalLinkAfterIdentity],
	);

	const getCloudExistingUrl = (siteUrl: string, templateId: string) => {
		if (productKey !== ProductKeys.CONFLUENCE) {
			return `${siteUrl}/jira/create-project?templateId=${templateId}`;
		}

		const templateSplit = templateId.split(':');

		if (templateSplit[0] === 'whiteboard') {
			return `${siteUrl}/wiki/login.action?os_destination=${encodeURIComponent(
				`/create-content/whiteboard?insertTemplateWithKey=${templateSplit[1]}&redirect=true&referer=${referer}`,
			)}`;
		}

		return `${siteUrl}/wiki/login.action?os_destination=%2Frest%2Fcreate-dialog%2F1.0%2Fcontent%2Fblueprint%2FgetGlobalFabricBlueprintDraft%3Fredirect%3Dtrue%26blueprintModule%3D${templateId}%26referer%3D${referer}`;
	};

	const tryToUseTemplate = useCallback(() => {
		if (!selectedTemplate.current) {
			return;
		}

		const { templateId, templateName } = selectedTemplate.current;

		if (siteUrls.length > 0) {
			setIsOpen(true);
		} else {
			if (redirectToIdentity && internalLinkAfterIdentity) {
				location.href = getIdentityUrl(templateId, templateName);
			} else {
				location.href = getCloudSignupUrl();
			}
		}
	}, [siteUrls, redirectToIdentity, internalLinkAfterIdentity, getIdentityUrl, getCloudSignupUrl]);

	const openModalForTemplate = useCallback(
		(template: TemplateData, isCallOnProductNav?: boolean) => {
			selectedTemplate.current = template;
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'linkButton',
				attributes: {
					templateName: template.templateName,
					label: template.templateName,
					path: location.pathname,
					event_type: 'ui',
					url: location.href,
					product: template.productKey ?? productKey,
					templateId: template.templateId,
					loginUserDetected: userProfile.isUserLoggedIn,
					trackEventName: getEventName('clicked-use-template'),
					eventContainer: isCallOnProductNav ? 'product-nav' : '',
				},
			}).fire();
			tryToUseTemplate();
		},
		[createAnalyticsEvent, userProfile.isUserLoggedIn, tryToUseTemplate, productKey],
	);

	const switchAccount = () => {
		if (!selectedTemplate.current) {
			return;
		}

		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'linkButton',
			attributes: {
				trackEventName: getEventName('clicked-switch-account'),
			},
		}).fire();

		const { templateId, templateName } = selectedTemplate.current;
		if (productKey === ProductKeys.CONFLUENCE) {
			const continueUrl = `${getWacIntegrationUrl()}${internalLinkAfterIdentity}?templateId=${templateId}&templateName=${encodeURIComponent(
				templateName,
			)}`;
			location.href = getIdSwitchUserUrl(continueUrl);
		} else {
			location.href = getIdSwitchUserUrl();
		}
	};

	const siteSelected = (site: string) => {
		createAnalyticsEvent({
			actionSubject: 'linkButton',
			action: 'clicked',
			attributes: {
				site,
				bluePrintModule: selectedTemplate.current?.templateId,
				eventName: 'project-created-(client)',
				createdUsing: 'templateWac',
				userId: userProfile.accountId,
				product: productKey,
				learnMore: referer === 'learnMore',
				trackEventName: getEventName('submitted-existing-site'),
			},
		}).fire();

		const cloudId = aisSites?.find((s) => s.url === site)?.cloudId;
		if (
			checkFeatureFlagGate(featureFlags.jiraFY27H1HoldoutExclusion) &&
			selectedTemplate.current?.templateId === 'jsw.agentic-development-with-cmp' &&
			analyticsClient &&
			cloudId
		) {
			fireHoldoutExclusionAnalytics({ analyticsClient, createAnalyticsEvent, cloudId });
		}

		if (selectedTemplate.current) {
			location.href = getCloudExistingUrl(site, selectedTemplate.current?.templateId);
		}
	};

	const newSiteSelected = () => {
		createAnalyticsEvent({
			actionSubject: 'linkButton',
			action: 'clicked',
			attributes: {
				bluePrintModule: selectedTemplate.current?.templateId,
				createdUsing: 'templateWac',
				userId: userProfile.accountId,
				product: productKey,
				learnMore: referer === 'learnMore',
				trackEventName: getEventName('submitted-new-site'),
			},
		}).fire();
		location.href = getCloudSignupUrl();
	};

	const closeModal = () => {
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'linkButton',
			attributes: {
				trackEventName: getEventName('clicked-modal-closed'),
			},
		}).fire();
		selectedTemplate.current = null;
		setIsOpen(false);
	};

	const siteChanged = () => {
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'linkButton',
			attributes: {
				trackEventName: getEventName('changed-selected-site'),
			},
		}).fire();
	};

	return (
		<SiteSelectorContext.Provider
			value={{
				openModalForTemplate,
			}}
		>
			{children}
			{isOpen && (
				<SiteSelectorModal
					isOpen={isOpen}
					header={
						selectedTemplate.current ? (
							<ProductHeader
								productKey={selectedTemplate.current.productKey}
								templateName={selectedTemplate.current.templateName}
								selectedLabel={selectedTemplateLabel}
							/>
						) : null
					}
					onSwitchAccountClick={switchAccount}
					onSiteSelected={siteSelected}
					onSiteChanged={siteChanged}
					onNewSiteSelected={newSiteSelected}
					onModalClose={closeModal}
					userEmail={userProfile.email || ''}
					sites={siteUrls}
					userName={userProfile.name || ''}
					avatarSrc={userProfile.picture}
				/>
			)}
		</SiteSelectorContext.Provider>
	);
};
